import { datumKort } from "@/lib/datum";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { currentWeek } from "@/lib/week";
import { LoadError, errMessage } from "@/components/LoadError";
import WeekPicker from "@/components/WeekPicker";
import { weekLabel } from "./overzichtUtils";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { AlertTriangle, MapPin, MapPinOff, CalendarClock, User, UserCheck, Inbox, CheckCircle2, NotebookPen } from "lucide-react";

const label = "text-[11px] font-semibold uppercase tracking-wider text-zinc-400";

function Tile({ title, value, tone, suffix }) {
  const toneCls = tone === "red" ? "text-rose-600" : tone === "emerald" ? "text-emerald-600" : "text-zinc-900";
  return (
    <div className={`rounded-md border px-4 py-3 ${tone === "red" ? "bg-rose-50 border-rose-200" : "bg-white border-zinc-200"}`}>
      <div className={label}>{title}</div>
      <div className={`font-mono text-3xl font-black tabular-nums ${toneCls}`}>{value}{suffix && <span className="text-zinc-300 text-2xl">{suffix}</span>}</div>
    </div>
  );
}

function OmCard({ o, inactief = false }) {
  const geheim = o.weekritmeTotal == null; // CEO/COO-ritme blijft dicht voor OM's
  const pct = geheim ? null : Math.round((o.weekritmeDone / (o.weekritmeTotal || 1)) * 100);
  const hasRood = (o.roodZonderAfspraak || o.roodZonderOpvolging).length > 0;
  return (
    <div
      data-testid={inactief ? `overzicht-inactief-${o.om}` : `overzicht-om-${o.om}`}
      className={`rounded-md border p-4 transition-colors ${inactief ? "bg-zinc-50 border-zinc-200 border-dashed" : "bg-white border-zinc-200 hover:border-zinc-300"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className={`font-heading font-black capitalize text-xl ${inactief ? "text-zinc-500" : "text-zinc-900"}`}>{o.om}</h2>
          {inactief && <span className="text-[10px] font-semibold normal-case px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-500">uit dienst</span>}
        </div>
        <div className="flex items-center gap-3">
          <span data-testid={`overzicht-voorbereid-${o.om}`} className="flex items-center gap-1 text-sm font-semibold text-zinc-500" title="Locaties met ingevulde voorbereiding">
            <NotebookPen className="h-3.5 w-3.5" /> {o.voorbereidCount ?? 0} voorbereid · {o.voorbereidVolledigCount ?? 0} volledig
          </span>
          <span className="font-mono text-sm font-bold text-zinc-500">{geheim ? "–" : `${pct}%`} weekritme</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Tile title="Weekritme" value={geheim ? "–" : o.weekritmeDone} suffix={geheim ? "" : `/${o.weekritmeTotal}`} />
        <Tile title="Locaties" value={o.locatiesTouched} />
        <Tile title="Rood" value={o.locatiesRoodTotaal ?? o.locatiesRood} />
      </div>
      {hasRood ? (
        <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2">
            <AlertTriangle className="h-3.5 w-3.5" /> Rood zonder afspraak
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(o.roodZonderAfspraak || o.roodZonderOpvolging).map((l) => (
              <span key={l} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white border border-rose-200 text-rose-700">{l}</span>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold uppercase tracking-wider">
          <CheckCircle2 className="h-3.5 w-3.5" /> Geen rood zonder afspraak
        </div>
      )}
    </div>
  );
}

export function ZonderOmPanel({ data, reload, compact = false }) {
  const [assignBusy, setAssignBusy] = useState(false);
  const [rowTarget, setRowTarget] = useState({});
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const activeOms = data.activeOms || [];
  // Schrijft de HELE mapping met de zojuist ontvangen versie; herlaadt altijd zodat
  // niets als toegewezen blijft staan dat niet echt is opgeslagen (fail-soft).
  const saveMapping = async (mapping, successMsg, onSuccess) => {
    setAssignBusy(true);
    try {
      await api.put("/config/location-oms", { value: mapping, baseVersion: data.locationOmsVersion });
      toast.success(successMsg);
      if (onSuccess) onSuccess();
    } catch (e) {
      if (e?.response?.status === 409) toast.error(e.response.data.detail);
      else toast.error(errMessage(e));
    } finally {
      setAssignBusy(false);
      reload();
    }
  };

  const assignOne = (loc, om) => {
    if (!om) return;
    const cur = data.locationOms?.[loc] || [];
    const mapping = { ...(data.locationOms || {}), [loc]: Array.from(new Set([...cur, om])).sort() };
    // Selectie pas wissen NA een geslaagde save, zodat bij een fout de keuze niet verdwijnt.
    saveMapping(mapping, `${loc} toegewezen aan ${om}`, () => setRowTarget((t) => ({ ...t, [loc]: "" })));
  };

  const assignAll = () => {
    const om = bulkTarget;
    if (!om) return;
    const locs = data.locatiesZonderOm || [];
    const mapping = { ...(data.locationOms || {}) };
    for (const loc of locs) {
      mapping[loc] = Array.from(new Set([...(mapping[loc] || []), om])).sort();
    }
    setBulkOpen(false); setBulkTarget("");
    saveMapping(mapping, `${locs.length} locatie(s) toegewezen aan ${om}`);
  };

  const pickCls = "px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-xs text-zinc-900 capitalize focus:outline-none focus:ring-2 focus:ring-blue-600";
  const assignBtnCls = "flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-zinc-950 text-white text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 transition-colors disabled:opacity-40";
  const aantal = (data.locatiesZonderOm || []).length;
  if (compact && aantal === 0) {
    return (
      <div data-testid="alle-locaties-gedekt" className="mb-3 md:mb-4 flex items-center gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs text-emerald-700 font-semibold">
        <CheckCircle2 className="h-3.5 w-3.5" /> Alle locaties hebben een actieve OM
      </div>
    );
  }
  return (
    <>
      <div data-testid="locaties-zonder-om-panel" className="bg-white rounded-[10px] border border-zinc-200 p-5 mb-3 md:mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-zinc-900 text-sm uppercase tracking-wider flex items-center gap-1.5">
            <MapPinOff className="h-4 w-4 text-amber-600" /> Locaties zonder actieve OM
          </h2>
          <span className="font-mono text-sm font-bold text-zinc-400" data-testid="zonder-om-count">{(data.locatiesZonderOm || []).length}</span>
        </div>
        {(data.locatiesZonderOm || []).length === 0 ? (
          <div data-testid="alle-locaties-gedekt" className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold uppercase tracking-wider">
            <CheckCircle2 className="h-3.5 w-3.5" /> Alle locaties hebben een actieve OM
          </div>
        ) : activeOms.length === 0 ? (
          <>
            <p className="text-xs text-zinc-500 mb-2.5">Deze locaties hebben geen actieve OM.</p>
            <div data-testid="geen-actieve-oms" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              Er zijn geen actieve OM's om aan toe te wijzen. Activeer of voeg eerst een OM toe via Instellingen → Gebruikers.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.locatiesZonderOm.map((l) => (
                <span key={l} data-testid={`zonder-om-${l}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800">{l}</span>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-zinc-500 mb-3">Niet toegewezen, of alleen aan een niet-actieve gebruiker — wijs hier direct een actieve OM toe.</p>
            <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-zinc-100">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Alle {data.locatiesZonderOm.length} tegelijk:</span>
              <select data-testid="bulk-assign-select" value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} className={pickCls}>
                <option value="">— Kies OM —</option>
                {activeOms.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <button data-testid="bulk-assign-btn" disabled={!bulkTarget || assignBusy} onClick={() => setBulkOpen(true)} className={assignBtnCls}>
                <UserCheck className="h-3.5 w-3.5" /> Wijs alle {data.locatiesZonderOm.length} toe
              </button>
            </div>
            <div className="space-y-1.5">
              {data.locatiesZonderOm.map((l) => (
                <div key={l} data-testid={`zonder-om-${l}`} className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 min-w-[130px] text-center">{l}</span>
                  <select data-testid={`assign-select-${l}`} value={rowTarget[l] || ""} onChange={(e) => setRowTarget((t) => ({ ...t, [l]: e.target.value }))} className={pickCls} disabled={assignBusy}>
                    <option value="">— Kies OM —</option>
                    {activeOms.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <button data-testid={`assign-btn-${l}`} disabled={!rowTarget[l] || assignBusy} onClick={() => assignOne(l, rowTarget[l])} className={assignBtnCls}>Toewijzen</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="bulk-assign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Alle {(data.locatiesZonderOm || []).length} locaties toewijzen aan {bulkTarget}?</AlertDialogTitle>
            <AlertDialogDescription>
              De volgende locaties krijgen <span className="font-semibold capitalize">{bulkTarget}</span> als OM. Eventuele andere (actieve) OM's op die locaties blijven behouden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-wrap gap-1.5 py-1" data-testid="bulk-assign-preview">
            {(data.locatiesZonderOm || []).map((l) => (
              <span key={l} className="text-xs px-2 py-1 rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700">{l}</span>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-assign-cancel">Annuleren</AlertDialogCancel>
            <button data-testid="bulk-assign-confirm" disabled={assignBusy} onClick={assignAll} className={assignBtnCls}>
              {assignBusy ? "Bezig…" : "Toewijzen"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function PerWeek() {
  const [week, setWeek] = useState(currentWeek());
  const [data, setData] = useState({ perOm: [], perOmInactief: [], openAfspraken: [], locatiesZonderOm: [] });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const load = () => {
    setLoaded(false);
    api.get(`/overzicht?week=${week}`)
      .then((r) => { setData(r.data); setLoaded(true); setLoadError(""); })
      .catch((e) => setLoadError(errMessage(e)));
  };
  useEffect(load, [week]);
  const ci = data.week === week ? data.callInfo : null;

  return (
    <div data-testid="overzicht-per-week">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 data-testid="overzicht-week-label" className="font-heading font-black text-lg text-zinc-900">{ci ? `Calls van ${ci.callLabelLang} · over week ${ci.overWeekNr}` : weekLabel(week)}</h2>
        <WeekPicker week={week} setWeek={setWeek} sub={ci ? `Call ${ci.callLabel} · over week ${ci.overWeekNr}` : undefined} />
      </div>
      <LoadError error={loadError} onRetry={load} testid="overzicht-load-error" className="mb-4" />

      {loadError ? null : !loaded ? (
        <div data-testid="overzicht-loading" className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center text-sm text-zinc-400">Laden…</div>
      ) : (
        <>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3 md:mb-4">
        {data.perOm.map((o) => <OmCard key={o.om} o={o} />)}
      </div>

      {(data.perOmInactief || []).length > 0 && (
        <div className="mb-3 md:mb-4" data-testid="overzicht-inactief-blok">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-zinc-400" />
            <h2 className="font-heading font-bold text-zinc-500 text-sm uppercase tracking-wider">Uit dienst — historie</h2>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.perOmInactief.map((o) => <OmCard key={o.om} o={o} inactief />)}
          </div>
        </div>
      )}

      {data.locationOmsVersion !== undefined && <ZonderOmPanel data={data} reload={load} />}

      <div data-testid="dekking-panel" className="bg-white rounded-[10px] border border-zinc-200 p-5 mb-3 md:mb-4">
        <h2 className="font-heading font-bold text-zinc-900 text-sm uppercase tracking-wider flex items-center gap-1.5 mb-3">
          <MapPin className="h-4 w-4 text-blue-600" /> Dekking deze week
        </h2>
        {Object.keys(data.coverage || {}).length === 0 ? (
          <p data-testid="dekking-leeg" className="text-xs text-zinc-500">Nog geen locatie-calls vastgelegd deze week.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-1.5">
            {Object.entries(data.coverage).sort(([a], [b]) => a.localeCompare(b)).map(([loc, oms]) => (
              <div key={loc} data-testid={`dekking-${loc}`} className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-semibold px-2.5 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700 min-w-[130px] text-center">{loc}</span>
                <span className="text-zinc-500 capitalize">{oms.join(", ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-[10px] border border-zinc-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
          <h2 className="font-heading font-bold text-zinc-900 text-sm uppercase tracking-wider">Openstaande afspraken om op te volgen</h2>
          <span className="font-mono text-sm font-bold text-zinc-400" data-testid="open-afspraken-count">{data.openAfspraken.length}</span>
        </div>
        {data.openAfspraken.length === 0 ? (
          <div className="p-10 text-center">
            <Inbox className="h-9 w-9 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">Geen openstaande afspraken.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {data.openAfspraken.map((a) => (
              <div key={a.id} className={`px-5 py-3 hover:bg-zinc-50 transition-colors ${a.overdue ? "bg-rose-50/60" : ""}`}>
                <p className="text-sm font-semibold text-zinc-900 whitespace-pre-line break-words">{a.wat}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
                  {a.locatie && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.locatie}</span>}
                  {a.deadline && <span className={`inline-flex items-center gap-1 font-mono ${a.overdue ? "text-rose-600 font-bold" : ""}`}><CalendarClock className="h-3.5 w-3.5" />{datumKort(a.deadline)}{a.overdue ? " · te laat" : ""}</span>}
                  <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />door {a.door}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
